import React, { useState, useRef, useEffect } from 'react';
import { X, ShieldCheck, ChevronDown } from 'lucide-react';
import { bankRequestOTP } from '@/api/Bank';
import { useTranslation } from 'react-i18next';
import bankList from "@/data/bank_list.json";

interface BankForm {
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  branch: string;
  setAsDefault: boolean;
}

interface AddBankModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (otp: string, bankData: BankForm) => void;
}

const AddBankModal: React.FC<AddBankModalProps> = ({ isOpen, onClose, onAdd }) => {
  const { t } = useTranslation();
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isBankListOpen, setIsBankListOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bankForm, setBankForm] = useState<BankForm>({
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    branch: '',
    setAsDefault: false
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsBankListOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleNext = () => {
    if (!bankForm.bankName || !bankForm.accountNumber || !bankForm.accountHolder) {
      return;
    }

    setIsSubmitting(true);
    bankRequestOTP()
      .then(() => {
        setShowOtpModal(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleConfirmOtp = () => {
    if (otp.length < 6) {
      return;
    }

    setIsSubmitting(true);
    onAdd(otp, bankForm);
    handleClose();
    setIsSubmitting(false);
  };

  const handleClose = () => {
    onClose();
    setBankForm({ bankName: '', accountNumber: '', accountHolder: '', branch: '', setAsDefault: false });
    setShowOtpModal(false);
    setOtp('');
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setBankForm((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Add Account Modal */}
      {!showOtpModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-md max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {t('profile.banking.addAccount', 'Thêm Tài Khoản Ngân Hàng')}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-red-600 dark:hover:text-red-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-8 py-6 space-y-4 overflow-y-auto max-h-[calc(90vh-180px)]">
              <div className="grid grid-cols-12 gap-4 items-center">
                <label className="col-span-4 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">
                  {t('profile.banking.bankName', 'Tên Ngân hàng')}
                </label>
                <div className="col-span-8 relative" ref={dropdownRef}>
                  <div
                    onClick={() => setIsBankListOpen(!isBankListOpen)}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all outline-none cursor-pointer flex items-center justify-between h-[42px]"
                  >
                    <span className={!bankForm.bankName ? "text-gray-500 dark:text-gray-400" : ""}>
                      {bankForm.bankName || t('profile.banking.selectBank', 'Chọn ngân hàng')}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isBankListOpen ? 'rotate-180' : ''}`} />
                  </div>

                  {isBankListOpen && (
                    <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-xl z-[1000] max-h-64 flex flex-col">
                      <div className="p-2 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 rounded-t-md">
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder={t("common.search", "Search...")}
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-primary placeholder-gray-400 dark:placeholder-gray-500"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto no-scrollbar flex-1 bg-white dark:bg-gray-800 rounded-b-md">
                        {bankList
                          .filter(
                            (bank) =>
                              bank.bank_name.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map((bank) => (
                            <div
                              key={bank.no}
                              onClick={() => {
                                setBankForm((prev) => ({ ...prev, bankName: bank.bank_name }));
                                setIsBankListOpen(false);
                                setSearchTerm("");
                              }}
                              className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-50 dark:border-gray-700/50 last:border-0 ${
                                bankForm.bankName === bank.bank_name
                                  ? "bg-primary/5 text-primary dark:bg-primary/20 dark:text-primary-light"
                                  : "text-gray-700 dark:text-gray-200"
                              }`}
                            >
                              <div className="font-medium">{bank.bank_name}</div>
                            </div>
                          ))}
                        {bankList.filter((b) =>
                          b.bank_name.toLowerCase().includes(searchTerm.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-3 text-sm text-center text-gray-500 dark:text-gray-400">
                            No results found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* <div className="grid grid-cols-12 gap-4 items-start">
                <label className="col-span-4 text-sm font-medium text-gray-600 dark:text-gray-400 text-right pt-3">
                  {t(
                    'profile.banking.branchName',
                    'Tên chi nhánh ngân hàng',
                  )}
                </label>
                <div className="col-span-8 relative">
                  <select
                    name="branch"
                    value={bankForm.branch}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-700 dark:text-white transition-all outline-none appearance-none cursor-pointer"
                  >
                    <option value="">
                      {t('profile.banking.selectBranch', 'Chọn chi nhánh')}
                    </option>
                    <option value="hcm">Chi nhánh TP.HCM</option>
                    <option value="hn">Chi nhánh Hà Nội</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div> */}

              <div className="grid grid-cols-12 gap-4 items-center">
                <label className="col-span-4 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">
                  {t('profile.banking.accountNumber', 'Số tài khoản')}
                </label>
                <div className="col-span-8">
                  <input
                    type="text"
                    name="accountNumber"
                    value={bankForm.accountNumber}
                    onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value.replace(/[^0-9]/g, '') })}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-700 dark:text-white transition-all outline-none"
                    placeholder=""
                  />
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4 items-start">
                <label className="col-span-4 text-sm font-medium text-gray-600 dark:text-gray-400 text-right pt-3">
                  {t(
                    'profile.banking.accountHolderName',
                    'Tên chủ tài khoản (Viết in hoa, không dấu)',
                  )}
                </label>
                <div className="col-span-8">
                  <input
                    type="text"
                    name="accountHolder"
                    value={bankForm.accountHolder}
                    onChange={handleInputChange}
                    maxLength={64}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-700 dark:text-white transition-all outline-none"
                    placeholder=""
                  />
                  <div className="text-right text-xs text-gray-400 mt-1.5">
                    {bankForm.accountHolder.length}/64
                  </div>
                </div>
              </div>

              {/* Set as Default */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-4"></div>
                <div className="col-span-8">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="setAsDefault"
                      checked={bankForm.setAsDefault}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary/20 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      {t(
                        'profile.banking.setAsDefault',
                        'Set as Default Receiving Account',
                      )}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-8 py-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={handleClose}
                className="px-8 py-3 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-all font-medium border border-gray-200 dark:border-gray-600"
              >
                {t('common.cancel', 'Hủy')}
              </button>
              <button
                onClick={handleNext}
                disabled={isSubmitting || !bankForm.bankName || !bankForm.accountNumber || !bankForm.accountHolder}
                className="px-8 py-3 bg-gradient-to-r from-primary to-primary-dark hover:shadow-lg hover:shadow-primary/30 text-white rounded-md transition-all font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {t('common.continue', 'Tiếp theo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-md max-w-md w-full shadow-2xl overflow-hidden">
            <div className="px-6 py-8 text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t('profile.banking.otpTitle', 'Xác thực OTP')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 px-4">
                {t('profile.banking.otpDesc', 'Vui lòng nhập mã OTP đã được gửi đến số điện thoại/email của bạn để xác nhận thêm tài khoản ngân hàng.')}
              </p>

              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="------"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-full px-4 py-3 border-2 border-gray-100 dark:border-gray-700 rounded-lg text-center text-3xl font-mono tracking-[0.5em] focus:border-primary outline-none transition-all dark:bg-gray-700 dark:text-white"
                />

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowOtpModal(false)}
                    className="flex-1 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all font-medium"
                  >
                    {t('common.cancel', 'Hủy')}
                  </button>
                  <button
                    onClick={handleConfirmOtp}
                    disabled={otp.length < 6 || isSubmitting}
                    className="flex-1 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-all font-medium disabled:opacity-50 shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
                  >
                    {isSubmitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {t('common.confirm', 'Xác nhận')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddBankModal;
